import { GameUiLayer, type PanelRequest, type PlayerSettings, sampleData } from '@mogzo/game-ui';
import type { AchievementView, GameUiLook, UiPanel, UiScreen, UiSize, UiView } from '@mogzo/shared';
import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { API_URL } from '../../lib/api';
import { TARGET_NAMES } from './model';

/** The view's real size, and how the frame around it looks. */
const STAGES: Record<UiView, { w: number; h: number; border: number; frame: string; bg: string }> =
  {
    'computer.game': { w: 1120, h: 670, border: 0, frame: 'rounded-[10px]', bg: '#000000' },
    'computer.splash': {
      w: 720,
      h: 600,
      border: 1,
      frame: 'rounded-[10px] border border-[#dddbe5]',
      bg: '#f5f2f9',
    },
    'tablet.game': {
      w: 768,
      h: 1024,
      border: 11,
      frame: 'rounded-[30px] border-[11px] border-[#0b0a1d]',
      bg: '#100c24',
    },
    'tablet.splash': {
      w: 768,
      h: 1024,
      border: 11,
      frame: 'rounded-[30px] border-[11px] border-[#0b0a1d]',
      bg: '#f5f2f9',
    },
    'phone.game': {
      w: 390,
      h: 844,
      border: 9,
      frame: 'rounded-[40px] border-[9px] border-[#0b0a1d]',
      bg: '#100c24',
    },
    'phone.splash': {
      w: 390,
      h: 844,
      border: 9,
      frame: 'rounded-[40px] border-[9px] border-[#0b0a1d]',
      bg: '#f5f2f9',
    },
  };

/** A stand-in game behind the panels, until games have cover pictures (M20). */
const STAND_IN: CSSProperties = {
  background:
    'radial-gradient(circle at 50% 42%, #2fb4a6 0 9%, rgba(47,180,166,0.25) 9.5% 16%, transparent 16.5%), radial-gradient(circle at 18% 22%, rgba(124,58,237,0.35), transparent 40%), linear-gradient(#1d1542, #0a0819)',
};

const SAMPLE_TOAST: AchievementView = {
  id: 'close-call',
  name: 'Close call',
  description: null,
  points: 10,
  hidden: false,
  steps: null,
  icon: `${API_URL}/v1/achievement-icons/zap.svg?color=violet`,
  progress: 1,
  unlocked: true,
  unlockedAt: null,
};

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Where the open panel (or the pop-up) is drawn, in the stage's unscaled pixels. */
function usePanelBox(
  host: RefObject<HTMLElement | null>,
  outer: RefObject<HTMLElement | null>,
  key: string,
) {
  const [box, setBox] = useState<Box | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` names what moves the panel
  useLayoutEffect(() => {
    const measure = () => {
      const found = host.current?.querySelector('[role="dialog"], [role="status"]');
      const frame = outer.current?.getBoundingClientRect();
      if (!found || !frame) return setBox(null);
      const r = found.getBoundingClientRect();
      setBox((current) => {
        const next = {
          left: r.left - frame.left,
          top: r.top - frame.top,
          width: r.width,
          height: r.height,
        };
        return current && JSON.stringify(current) === JSON.stringify(next) ? current : next;
      });
    };
    measure();
    // Again once the panel's entrance has finished and its scores have loaded.
    const timers = [260, 700].map((ms) => setTimeout(measure, ms));
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    const panel = host.current?.querySelector('[role="dialog"], [role="status"]');
    if (panel) observer?.observe(panel);
    if (outer.current) observer?.observe(outer.current);
    return () => {
      for (const timer of timers) clearTimeout(timer);
      observer?.disconnect();
    };
  }, [key]);
  return box;
}

/**
 * The preview: the view at its real size, scaled to fit, with MOGZO's real panels over a
 * stand-in game (or a splash in a post). The panel being edited is outlined; with Theme open,
 * clicking the panel edits it.
 */
export function Stage({
  look,
  size,
  screen,
  shown,
  selected,
  onPick,
  toolbar,
}: {
  look: GameUiLook;
  size: UiSize;
  screen: UiScreen;
  shown: UiPanel;
  /** The shown panel is the one being edited (not Theme). */
  selected: boolean;
  onPick: (panel: UiPanel) => void;
  toolbar: ReactNode;
}) {
  const view: UiView = `${size}.${screen}`;
  const stage = STAGES[view];
  const data = useMemo(() => sampleData(API_URL), []);
  const [settings, setSettings] = useState<PlayerSettings>({ sound: true, popups: true });
  const panel = useMemo<PanelRequest | null>(
    () =>
      shown === 'popup'
        ? null
        : shown === 'match'
          ? { kind: 'match', open: { how: 'invite', mode: 'duel' } }
          : { kind: shown },
    [shown],
  );
  const area = useRef<HTMLDivElement>(null);
  const outer = useRef<HTMLDivElement>(null);
  const host = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState({ w: 900, h: 700 });

  useLayoutEffect(() => {
    const element = area.current;
    if (!element) return;
    const measure = () => setRoom({ w: element.clientWidth, h: element.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const outerW = stage.w + stage.border * 2;
  const outerH = stage.h + stage.border * 2;
  const k = Math.max(0.2, Math.min(1, (room.w - 56) / outerW, (room.h - 56) / outerH));
  const box = usePanelBox(host, outer, `${JSON.stringify(look)}|${view}|${shown}|${k}`);

  const phone = size === 'phone';
  const splash = screen === 'splash';
  const layer = (
    <div ref={host} className="absolute inset-0">
      <GameUiLayer
        look={look}
        device={size}
        screen={screen}
        gameName="Your game"
        panel={panel}
        toast={shown === 'popup' ? SAMPLE_TOAST : null}
        data={data}
        settings={shown === 'popup' ? { ...settings, popups: true } : settings}
        onSettings={setSettings}
        onOpen={(next) => onPick(next.kind === 'room' ? 'match' : next.kind)}
        onClose={() => undefined}
        onRestart={() => undefined}
        onExit={() => undefined}
        signInUrl="#"
        signUpUrl="#"
        besidePauseButton={phone && !splash}
        autoFocus={false}
      />
    </div>
  );

  // The tag sits under a panel at the top or side, and over the others.
  const below =
    shown === 'popup'
      ? (box?.top ?? 0) < 60
      : !phone && shown !== 'pause' && shown !== 'signin' && shown !== 'match';
  const tagStyle: CSSProperties = below ? { bottom: -28 } : { top: -28 };
  const name = TARGET_NAMES[shown];

  return (
    <main aria-label="Preview" className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex min-h-10 flex-none flex-wrap items-center gap-1 border-b border-[#dddbe5] bg-white px-3 py-1 text-xs">
        {toolbar}
        <span className="ml-auto font-mono text-[11.5px] text-[#8a859c]">
          {stage.w} × {stage.h} · {Math.round(k * 100)}%
        </span>
      </div>
      <div ref={area} className="grid min-h-0 flex-1 place-items-center overflow-hidden p-7">
        <div
          ref={outer}
          style={{ width: outerW * k, height: outerH * k }}
          className="relative flex-none"
        >
          <div
            style={{
              width: outerW,
              height: outerH,
              transform: `scale(${k})`,
              transformOrigin: '0 0',
              background: stage.bg,
            }}
            className={`absolute top-0 left-0 overflow-hidden shadow-[0_18px_50px_rgba(27,21,47,0.22)] ${stage.frame}`}
          >
            <div
              style={{ width: stage.w, height: stage.h }}
              className="relative flex flex-col overflow-hidden text-[15px] text-ink"
            >
              {splash ? (
                <>
                  {size !== 'computer' && (
                    <div className="flex h-[52px] flex-none items-center justify-between border-b border-divider bg-white px-4">
                      <span className="font-display text-[22px] font-bold">mogzo</span>
                      <span className="size-[30px] rounded-full bg-line" />
                    </div>
                  )}
                  <div className={`flex min-h-0 flex-1 flex-col ${phone ? 'p-3' : 'p-4'}`}>
                    <div className="flex flex-col gap-2.5 rounded-[18px] border border-divider bg-white p-3.5">
                      <div className="flex items-center gap-2.5">
                        <span className="grid size-9 flex-none place-items-center rounded-full bg-brand font-bold">
                          Q
                        </span>
                        <span className="flex min-w-0 flex-col">
                          <span className="flex items-center gap-1.5 text-sm font-bold">
                            Quarkfox
                            <span className="rounded-full bg-ink px-[7px] py-px text-[11px] text-white">
                              Developer
                            </span>
                          </span>
                          <span className="text-xs text-muted">13 minutes ago</span>
                        </span>
                        <span className="ml-auto text-xs text-muted">Pinned</span>
                      </div>
                      <span className="text-lg font-bold">Build #11 is up</span>
                      <div
                        style={STAND_IN}
                        className={`relative overflow-hidden rounded-[14px] ${
                          phone ? 'aspect-[4/5]' : 'aspect-video'
                        }`}
                      >
                        {!phone && layer}
                      </div>
                      <span className="text-[13px] text-muted">GG 4 · 2 comments</span>
                    </div>
                  </div>
                  {/* On a phone, a splash's panels cover the feed. */}
                  {phone && layer}
                </>
              ) : (
                <>
                  <div style={STAND_IN} className="relative min-h-0 flex-1 overflow-hidden">
                    {phone && (
                      <span
                        aria-hidden="true"
                        className="absolute top-3 left-3 grid size-10 place-items-center rounded-full bg-black/40 text-white"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <rect x="6" y="5" width="4" height="14" rx="1" />
                          <rect x="14" y="5" width="4" height="14" rx="1" />
                        </svg>
                      </span>
                    )}
                    {layer}
                  </div>
                  {!phone && (
                    <div className="flex h-10 flex-none items-center px-2.5 text-white">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <rect x="6" y="5" width="4" height="14" rx="1" />
                        <rect x="14" y="5" width="4" height="14" rx="1" />
                      </svg>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {box && selected && (
            <div
              aria-hidden="true"
              style={{
                left: box.left - 4,
                top: box.top - 4,
                width: box.width + 8,
                height: box.height + 8,
              }}
              className="pointer-events-none absolute z-10 rounded-[4px] border-2 border-[#6d4aff]"
            >
              <span
                style={tagStyle}
                className="absolute -left-0.5 inline-flex h-[22px] items-center rounded bg-[#6d4aff] px-2 text-xs font-semibold whitespace-nowrap text-white"
              >
                {name}
              </span>
            </div>
          )}
          {box && !selected && (
            <button
              type="button"
              aria-label={`Style only the ${name.toLowerCase()}`}
              title={`Style only the ${name.toLowerCase()}`}
              onClick={() => onPick(shown)}
              style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
              className="absolute z-10 cursor-pointer rounded-[4px] border-2 border-dashed border-[#6d4aff] bg-[#6d4aff]/[0.06]"
            >
              <span
                style={tagStyle}
                className="absolute -left-0.5 inline-flex h-[22px] items-center rounded bg-[#6d4aff] px-2 text-xs font-semibold whitespace-nowrap text-white"
              >
                Edit {name.toLowerCase()}
              </span>
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
